import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { articles, CATEGORIES, getCategory, formatDate, SITE } from "@/lib/site";
import { ArticleCard } from "@/components/ArticleCard";
import { AdSlot } from "@/components/AdSlot";
import { Newsletter } from "@/components/Newsletter";
import { useSEO } from "@/lib/useSEO";

export default function Home() {
  useSEO({
    title: "Ruta Inteligente — Viaja más por menos | Guías de viajes low cost",
    description:
      "Guías, trucos y destinos para viajar low cost. Vuelos baratos, alojamiento económico y consejos para gastar menos en cada viaje.",
    canonical: SITE.url,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: SITE.name,
      url: SITE.url,
      description: SITE.description,
      publisher: { "@type": "Organization", name: SITE.name, url: SITE.url },
      potentialAction: {
        "@type": "SearchAction",
        target: `${SITE.url}/?q={search_term_string}`,
        "query-input": "required name=search_term_string",
      },
    },
  });

  const [hero, ...rest] = articles;
  const featured = rest.slice(0, 2);
  const latest = rest.slice(2, 11);

  return (
    <div>
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-10 pb-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <Link
            to={`/articulo/${hero.slug}`}
            data-testid="hero-article"
            className="lg:col-span-8 group fade-up"
          >
            <div className="relative overflow-hidden rounded-lg aspect-[16/10]">
              <img src={hero.image} alt={hero.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
              <div className="absolute bottom-0 left-0 p-6 sm:p-10 text-white">
                <span className="text-xs uppercase tracking-[0.2em] bg-brand-terra px-3 py-1 rounded-full">
                  {getCategory(hero.categorySlug)?.short}
                </span>
                <h1 className="font-heading text-3xl sm:text-5xl font-semibold leading-tight mt-4 max-w-2xl group-hover:text-brand-surfaceAlt transition-colors">
                  {hero.title}
                </h1>
                <p className="text-white/80 mt-3 max-w-xl hidden sm:block">{hero.excerpt}</p>
                <span className="inline-flex items-center gap-2 mt-4 text-sm font-semibold">
                  Leer artículo <ArrowRight size={16} />
                </span>
              </div>
            </div>
          </Link>

          <div className="lg:col-span-4 flex flex-col gap-8">
            {featured.map((a, i) => (
              <ArticleCard key={a.slug} article={a} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORIES STRIP */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex flex-wrap gap-3">
          {CATEGORIES.map((c) => (
            <Link
              key={c.slug}
              to={`/categoria/${c.slug}`}
              data-testid={`home-cat-${c.slug}`}
              className="px-4 py-2 rounded-full border border-brand-line bg-brand-surface text-sm font-medium text-brand-ink hover:bg-brand-forest hover:text-white hover:border-brand-forest transition-all"
            >
              {c.name}
            </Link>
          ))}
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <AdSlot />
      </div>

      {/* LATEST */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-brand-terra mb-2">Lo último</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-brand-ink">Artículos recientes</h2>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
          {latest.map((a, i) => (
            <ArticleCard key={a.slug} article={a} index={i} />
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6">
        <Newsletter />
      </section>

      {/* CATEGORY HIGHLIGHTS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-brand-ink mb-8">Explora por tema</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {CATEGORIES.map((c) => {
            const a = articles.find((x) => x.categorySlug === c.slug);
            return (
              <Link
                key={c.slug}
                to={`/categoria/${c.slug}`}
                className="group relative overflow-hidden rounded-lg aspect-[3/2]"
              >
                <img src={a?.image} alt={c.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-black/45 group-hover:bg-black/30 transition-colors" />
                <div className="absolute inset-0 flex flex-col justify-end p-6 text-white">
                  <h3 className="font-heading text-2xl font-semibold">{c.name}</h3>
                  <p className="text-sm text-white/80">{c.desc}</p>
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
}
